import { fetchPaginated } from '@/helpers/utils';
import { gql } from '@apollo/client';
import { IMeetingPlacePageEventItem } from '../fragments/meetingPlacePageEventItem';
import { MeetingPlacePageTenantFoodBevItemMinifiedLogoFragment } from '../fragments/meetingPlacePageTenantFoodBevItem';
import { MeetingPlacePageTenantEntertainmentItemMinifiedLogoFragment } from '../fragments/meetingPlacePageTenantEntertainmentItem';
import { MeetingPlacePageTenantShopItemMinifiedLogoFragment } from '../fragments/meetingPlacePageTenantShopItem';

export interface IActiveEventsWithTenants {
  meetingPlacePageHome: {
    meetingPlace: {
      occationListPage: {
        occationsCollection: {
          items: ActiveEventItem[];
        };
      };
      eventListPage: {
        eventItemsCollection: {
          items: ActiveEventItem[];
        };
      };
    };
  };
}

export type ActiveEventItem = {
  sys: {
    id: string;
  };
  startDate: string;
  endDate: string;
  tenant: {
    sys: {
      id: string;
    };
  };
};

export const TENANT_IDS_FROM_EVENT_FRAGMENT = gql`
  ${MeetingPlacePageTenantShopItemMinifiedLogoFragment}
  ${MeetingPlacePageTenantEntertainmentItemMinifiedLogoFragment}
  ${MeetingPlacePageTenantFoodBevItemMinifiedLogoFragment}
  fragment TenantIdsFromEvent on _Node {
    ... on MeetingPlacePageTenantEntertainment {
      ...MeetingPlacePageTenantEntertainmentItemMinifiedLogo
    }
    ... on MeetingPlacePageTenantShop {
      ...MeetingPlacePageTenantShopItemMinifiedLogo
    }
    ... on MeetingPlacePageTenantFoodBeverage {
      ...MeetingPlacePageTenantFoodBevItemMinifiedLogo
    }
  }
`;

export const GET_ACTIVE_EVENTS_FROM_OCCATIONS_WITH_TENANTS = gql`
  ${TENANT_IDS_FROM_EVENT_FRAGMENT}
  query getActiveEventsFromOccationsWithTenants(
    $id: String!
    $preview: Boolean!
    $limit: Int!
    $skip: Int!
  ) {
    meetingPlacePageHome(id: $id, preview: $preview) {
      meetingPlace {
        ... on LibraryMeetingPlace {
          occationListPage {
            occasionsCollection(limit: $limit, skip: $skip) {
              total
              items {
                ... on MeetingPlacePageEvent {
                  sys {
                    id
                  }
                  startDate
                  endDate
                  tenant {
                    ...TenantIdsFromEvent
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_ACTIVE_EVENTS_FROM_EVENT_LIST_WITH_TENANTS = gql`
  ${TENANT_IDS_FROM_EVENT_FRAGMENT}
  query getActiveEventsFromEventListWithTenants(
    $id: String!
    $preview: Boolean!
    $limit: Int!
    $skip: Int!
  ) {
    meetingPlacePageHome(id: $id, preview: $preview) {
      meetingPlace {
        ... on LibraryMeetingPlace {
          eventListPage {
            eventItemsCollection(limit: $limit, skip: $skip) {
              total
              items {
                sys {
                  id
                }
                startDate
                endDate
                tenant {
                  ...TenantIdsFromEvent
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getActiveEventsWithTenants = async (
  homePageContentfulId: string,
  preview: boolean,
) => {
  const { getApolloClient } = await import('../apolloClient');
  const client = getApolloClient(preview);

  const today = new Date();

  const [eventsFromOccasions, eventsFromListPage] = await Promise.all([
    fetchPaginated<IMeetingPlacePageEventItem>(
      client,
      GET_ACTIVE_EVENTS_FROM_OCCATIONS_WITH_TENANTS,
      (res) =>
        res?.data?.meetingPlacePageHome?.meetingPlace?.occationListPage
          ?.occasionsCollection,
      { id: homePageContentfulId, preview },
    ),

    fetchPaginated<IMeetingPlacePageEventItem>(
      client,
      GET_ACTIVE_EVENTS_FROM_EVENT_LIST_WITH_TENANTS,
      (res) =>
        res?.data?.meetingPlacePageHome?.meetingPlace?.eventListPage
          ?.eventItemsCollection,
      { id: homePageContentfulId, preview, today },
    ),
  ]);

  // Combine & dedupe + filter
  const combined = [...eventsFromOccasions, ...eventsFromListPage];

  const validEvents = combined
    ?.filter((event): event is IMeetingPlacePageEventItem => {
      if (
        !event ||
        !event.startDate ||
        !event.endDate ||
        !event.tenant?.sys?.id
      )
        return false;
      // const start = new Date(event?.startDate);
      // const end = new Date(event?.endDate);
      // return start <= today || end >= today;
      return true;
    })
    ?.filter(
      (event, index, arr) =>
        arr.findIndex((e) => e.sys.id === event.sys.id) === index,
    );
  // ?.map((event) => event?.sys?.id);

  return validEvents;
};
